import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../../hooks/useDebounce";
import { useI18n } from "../../../hooks/useTranslations";
import useItemApi from "../../../http/item/api";
import { Item } from "../../../http/item/types";
import { ProcessingRequestFinalItem } from "../../../http/processingRequestFinalItem/types";
import { ProcessingRequestInitialItem } from "../../../http/processingRequestInitialItem/types";
import { PaginationQuery } from "../../../types";
import { getItemDescription } from "../../../utils";
import SvgAdd from "../../icons/Add";
import SvgDelete from "../../icons/Delete";
import SvgEdit from "../../icons/Edit";
import Button from "../../ui/Button";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import Numeric from "../../ui/Numeric";
import reactSelectStyles from "../../ui/ReactSelectUtils";
import SubmitBar from "../../ui/SubmitBar";
import Table from "../../ui/Table";
import styles from "./ItemsForm.module.css";
import SideBar, { SideBarBody } from "./SideBar";

export type ItemsFormHandle = {
  validate: () => Promise<boolean>;
};

type ItemsFormBody = {
  finalItem: Item | null;
  finalItemQuantity: number | null;
  initialItem: Item | null;
  initialItemQuantity: number | null;
};

type ItemsFormProps = {
  onChange: (finalItems: ProcessingRequestFinalItem[], valid: boolean) => Promise<void>;
  allowQuantityVariation?: boolean;
  initialItemIsFinalItem?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isKitRequest: boolean;
};

const ItemsForm = forwardRef<ItemsFormHandle, ItemsFormProps>(
  ({ onChange, allowQuantityVariation, initialItemIsFinalItem, isDisabled, isLoading, isKitRequest }, ref) => {
    const i18n = useI18n();

    const { searchItems } = useItemApi();

    const [defaultItemsOptions, setDefaultItemsOptions] = useState<Item[]>([]);
    const [isSideBarOpen, setIsSideBarOpen] = useState<boolean>(false);
    const [itemToEdit, setItemToEdit] = useState<SideBarBody>();

    const [finalItems, setFinalItems] = useState<ProcessingRequestFinalItem[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>("");

    return (
      <>
        <Form<ItemsFormBody> disabled={isDisabled} mode="onChange">
          {({ control, watch, setError, clearErrors, trigger, setValue }) => {
            useImperativeHandle(ref, () => ({
              validate: async () => {
                setErrorMessage("");

                if (!isKitRequest) {
                  const hasInvalidItems = finalItems.some((fi) => fi.initialItems?.length !== 1);

                  if (hasInvalidItems) {
                    setErrorMessage(i18n.t.processingRequests.errors.finalItemMoreInitialItems);
                    await trigger();
                    return false;
                  }
                } else {
                  if (!finalItems[0]?.initialItems?.length) {
                    setErrorMessage(i18n.t.processingRequests.errors.missingKitComponents);
                    await trigger();
                    return false;
                  }

                  if (!finalItems || !finalItems[0]) {
                    setErrorMessage(i18n.t.processingRequests.errors.missingFinalItem);
                    await trigger();
                    return false;
                  }
                }
                if (finalItems.length === 0) {
                  setErrorMessage(i18n.t.processingRequests.errors.missingFinalItem);
                  await trigger();
                  return false;
                }
                return await trigger();
              },
            }));

            const submit = async (finalItems: ProcessingRequestFinalItem[]) => {
              const res = await trigger();
              onChange(finalItems, res);
            };

            useEffect(() => {
              setErrorMessage("");
              setFinalItems([]);

              const fi = watch("finalItem");
              if (fi?.isKit && !!fi.kitComponents.length) {
                addFinalItems();
              }

              if (!fi?.isKit) {
                setValue("finalItem", null);
              }
              submit([]);
            }, [isKitRequest]);

            const addFinalItems = () => {
              const finalItem = watch("finalItem");
              const initialItem = watch("initialItem");
              const finalItemQuantity = watch("finalItemQuantity");
              const initialItemQuantity = watch("initialItemQuantity");

              setErrorMessage("");
              clearErrors();
              if (!isKitRequest) {
                if (!initialItem && !initialItemIsFinalItem) {
                  setError("initialItem", { message: i18n.t.common.fieldCannotBeEmpty });
                  return;
                }

                if ((!initialItemQuantity || initialItemQuantity === 0) && allowQuantityVariation) {
                  setError("initialItemQuantity", { message: i18n.t.common.fieldCannotBeEmpty });
                  return;
                }

                if (!initialItemIsFinalItem && initialItem?.id === finalItem?.id) {
                  setError("finalItem", { message: i18n.t.errors.finalItemCannotMatchInitialItem });
                  return;
                }

                if (finalItems.find((x) => x.item.id === finalItem?.id)) {
                  setError("finalItem", { message: i18n.t.items.errors.alreadyAdded });
                  return;
                }
              }

              if (!finalItem) {
                setError("finalItem", { message: i18n.t.common.fieldCannotBeEmpty });
                return;
              }

              if (!finalItemQuantity || finalItemQuantity == 0) {
                setError("finalItemQuantity", { message: i18n.t.common.fieldCannotBeEmpty });
                return;
              }
              const updatedItems = isKitRequest
                ? [
                    {
                      id: "",
                      requestId: 0,
                      item: finalItem,
                      quantity: finalItemQuantity ?? 1,
                      cost: 0,
                      initialItems: finalItem.kitComponents.map((kc) => ({
                        id: "",
                        requestId: 0,
                        cost: 0,
                        item: kc.component,
                        quantity: Math.ceil((finalItemQuantity / finalItem?.factor) * kc.quantity),
                      })),
                    },
                  ]
                : [
                    ...finalItems,
                    {
                      id: "",
                      requestId: 0,
                      item: finalItem,
                      quantity: finalItemQuantity ?? 1,
                      cost: 0,
                      initialItems: [
                        {
                          id: "",
                          requestId: 0,
                          cost: 0,
                          item: initialItemIsFinalItem ? finalItem : initialItem!,
                          quantity: allowQuantityVariation ? (initialItemQuantity ?? 1) : (finalItemQuantity ?? 1),
                        },
                      ],
                    },
                  ];
              setFinalItems(updatedItems);
              submit(updatedItems);
            };

            const loadKits = (q: PaginationQuery, signal: AbortSignal | undefined): Promise<Item[]> =>
              searchItems(q, signal, true);

            return (
              <>
                <Controller
                  name="finalItem"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.t.common.finalItem} error={fieldState.error}>
                      {({ labelId, isInvalid }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef<AbortController | undefined>(undefined);
                        const loadOptions = createLoadOptions<Item>(
                          aborter,
                          searchTerm,
                          isKitRequest ? loadKits : searchItems,
                        );

                        useEffect(() => {
                          loadOptions("").then(setDefaultItemsOptions);
                        }, [isKitRequest]);

                        return (
                          <AsyncSelect<Item>
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultItemsOptions}
                            loadOptions={(st) => loadOptions(st)}
                            getOptionLabel={(o) => `${o.code} - ${getItemDescription(o, i18n.language)}`}
                            getOptionValue={(o) => o.id}
                            placeholder={i18n.t.common.select + "..."}
                            isDisabled={isDisabled || isLoading}
                            styles={reactSelectStyles(isInvalid)}
                            isClearable
                            value={field.value || null}
                            onChange={(value) => {
                              field.onChange(value);

                              !isKitRequest &&
                                value?.kitComponents.length == 1 &&
                                value.kitComponents[0]?.component &&
                                setValue("initialItem", value.kitComponents[0]?.component);

                              clearErrors();
                              isKitRequest && addFinalItems();
                            }}
                          />
                        );
                      }}
                    </FormField>
                  )}
                />

                <Controller
                  name="finalItemQuantity"
                  control={control}
                  rules={{
                    required: true,
                    min: {
                      value: 1,
                      message: i18n.t.common.fieldCannotBeEmpty,
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.t.common.quantity} error={fieldState.error}>
                      {({ labelId, isInvalid }) => {
                        const finalItem = watch("finalItem");

                        return (
                          <Numeric
                            {...field}
                            id={labelId}
                            min={1}
                            suffix={` ${finalItem?.unitOfMeasure || i18n.t.common.pz}`}
                            allowNegative={false}
                            decimalScale={0}
                            isInvalid={isInvalid}
                            disabled={isDisabled || isLoading}
                            onBlur={(value) => {
                              const quantity = value ?? 0;
                              setValue("finalItemQuantity", quantity);
                              clearErrors();
                              isKitRequest && addFinalItems();
                            }}
                          />
                        );
                      }}
                    </FormField>
                  )}
                />

                <>
                  <Controller
                    name={`initialItem`}
                    control={control}
                    rules={{ required: !isKitRequest && !initialItemIsFinalItem }}
                    render={({ field, fieldState }) => (
                      <FormField
                        label={i18n.t.common.initialItem}
                        error={fieldState.error}
                        hidden={isKitRequest || initialItemIsFinalItem}
                      >
                        {({ labelId, isInvalid }) => {
                          const searchTerm = useRef("");
                          const aborter = useRef<AbortController | undefined>(undefined);
                          const loadOptions = createLoadOptions<Item>(aborter, searchTerm, searchItems);

                          useEffect(() => {
                            loadOptions(searchTerm.current).then(setDefaultItemsOptions);
                          }, []);

                          return (
                            <AsyncSelect<Item>
                              {...field}
                              inputId={labelId}
                              defaultOptions={defaultItemsOptions}
                              loadOptions={(st) => loadOptions(st)}
                              getOptionLabel={(o) => `${o.code} - ${getItemDescription(o, i18n.language)}`}
                              getOptionValue={(o) => o.id}
                              placeholder={i18n.t.common.select + "..."}
                              isDisabled={isDisabled || isLoading || isKitRequest}
                              isClearable
                              styles={reactSelectStyles(isInvalid)}
                              onChange={(value) => {
                                field.onChange(value);
                              }}
                            />
                          );
                        }}
                      </FormField>
                    )}
                  />

                  <Controller
                    name={`initialItemQuantity`}
                    control={control}
                    rules={{
                      required: allowQuantityVariation && !isKitRequest,
                      min: {
                        value: 1,
                        message: i18n.t.common.fieldCannotBeEmpty,
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <FormField
                        label={i18n.t.common.initialItemQuantity}
                        error={fieldState.error}
                        hidden={!allowQuantityVariation || isKitRequest}
                      >
                        {({ labelId, isInvalid }) => {
                          const initialItem = watch("initialItem");

                          return (
                            <Numeric
                              {...field}
                              id={labelId}
                              min={1}
                              decimalScale={0}
                              allowNegative={false}
                              isInvalid={isInvalid}
                              suffix={` ${initialItem?.unitOfMeasure || i18n.t.common.pz}`}
                              disabled={isKitRequest}
                              onBlur={(value) => {
                                const quantity = value ?? 0;
                                setValue("initialItemQuantity", quantity);
                                clearErrors();
                                isKitRequest && addFinalItems();
                              }}
                            />
                          );
                        }}
                      </FormField>
                    )}
                  />
                  {!isKitRequest && (
                    <div className={styles.fixSpaces}>
                      <SubmitBar>
                        <Button
                          buttonProps={{
                            onClick: () => {
                              setErrorMessage("");
                              addFinalItems();
                            },
                          }}
                        >
                          {i18n.t.common.add}
                        </Button>
                      </SubmitBar>
                    </div>
                  )}
                </>

                {isKitRequest && (
                  <div>
                    <div className={styles.tableHead}>
                      <Button
                        buttonProps={{
                          onClick: () => setIsSideBarOpen(!isSideBarOpen),
                        }}
                        glyph={SvgAdd}
                      >
                        {i18n.t.common.add}
                      </Button>
                      {i18n.t.common.components}
                    </div>

                    <div className={styles.scrollContainer}>
                      <Table<ProcessingRequestInitialItem>
                        className={styles.formTable}
                        head={
                          <tr>
                            <th>{i18n.t.common.item}</th>
                            <th>{i18n.t.common.description}</th>
                            <th>{i18n.t.common.quantity}</th>
                            <th></th>
                          </tr>
                        }
                        renderRow={(item) => (
                          <>
                            <td align="center">{item.item.code}</td>
                            <td align="center">{getItemDescription(item.item, i18n.language)}</td>
                            <td align="center">
                              {item.quantity ?? 1} {item.item.unitOfMeasure || i18n.t.common.pz}
                            </td>
                          </>
                        )}
                        rowActions={[
                          {
                            title: i18n.t.common.edit,
                            onClick: (item) => {
                              setIsSideBarOpen(true);
                              setItemToEdit({
                                initialItem: item.item,
                                initialQuantity: item.quantity,
                              });
                              submit(finalItems);
                            },
                            icon: SvgEdit,
                          },
                          {
                            title: i18n.t.common.delete,
                            onClick: (itemToDelete) => {
                              if (finalItems[0]) {
                                const firstItem = finalItems[0];

                                const updatedInitialItems = firstItem.initialItems.filter(
                                  (x) => x.item.id !== itemToDelete.item.id,
                                );

                                const updatedItems: ProcessingRequestFinalItem[] = [
                                  {
                                    ...firstItem,
                                    initialItems: updatedInitialItems,
                                  },
                                ];

                                setFinalItems(updatedItems);
                                submit(updatedItems);
                              }
                            },

                            color: "red",
                            icon: SvgDelete,
                            isDivider: true,
                          },
                        ]}
                        items={finalItems[0]?.initialItems ?? []}
                      />
                    </div>
                  </div>
                )}

                {finalItems.length > 0 && !isKitRequest && (
                  <div className={styles.scrollContainer}>
                    <Table
                      className={styles.formTable}
                      head={
                        <>
                          <tr>
                            <th colSpan={2}>{i18n.t.common.initialItem}</th>
                            <th colSpan={3}>{i18n.t.common.finalItem}</th>
                          </tr>
                          <tr>
                            <th>{i18n.t.common.item}</th>
                            <th className={styles.borderRight}>{i18n.t.common.quantity}</th>
                            <th>{i18n.t.common.item}</th>
                            <th>{i18n.t.common.quantity}</th>
                            <th></th>
                          </tr>
                        </>
                      }
                      renderRow={(finalItem) => (
                        <>
                          {finalItem.initialItems.map((initialItem, i) => (
                            <React.Fragment key={i}>
                              <td align="center">{initialItem.item.code}</td>
                              <td align="center" className={styles.borderRight}>
                                {initialItem.quantity + " " + (initialItem.item.unitOfMeasure || i18n.t.common.pz)}
                              </td>
                            </React.Fragment>
                          ))}
                          <td align="center">{finalItem.item.code}</td>
                          <td align="center">
                            {finalItem.quantity + " " + (finalItem.item.unitOfMeasure || i18n.t.common.pz)}
                          </td>
                        </>
                      )}
                      rowActions={[
                        {
                          title: i18n.t.common.delete,
                          onClick: (itemToDelete) => {
                            const updatedItems = finalItems.filter((fi) => fi.item.id !== itemToDelete.item.id);
                            setFinalItems(updatedItems);
                            submit(updatedItems);
                          },
                          color: "red",
                          icon: SvgDelete,
                        },
                      ]}
                      items={finalItems}
                    />
                  </div>
                )}
                {errorMessage && (
                  <FormField error={{ type: "value", message: errorMessage }}>
                    <></>
                  </FormField>
                )}
              </>
            );
          }}
        </Form>
        <SideBar
          item={itemToEdit?.initialItem}
          alreadyPresentItemIds={finalItems[0]?.initialItems.map((x) => x.item.id) || []}
          quantity={itemToEdit?.initialQuantity ?? undefined}
          isSideBarOpen={isSideBarOpen && isKitRequest}
          onClose={() => {
            setIsSideBarOpen(false);
            setItemToEdit(undefined);
          }}
          onSave={async (formBody) => {
            if (finalItems[0]) {
              const updatedInitialItems = itemToEdit
                ? finalItems[0].initialItems.map((x) =>
                    x.item.id === itemToEdit.initialItem?.id
                      ? {
                          ...x,
                          quantity: formBody.initialQuantity ?? 0,
                        }
                      : x,
                  )
                : [
                    ...(finalItems[0].initialItems ?? []),
                    {
                      id: "",
                      requestId: 0,
                      item: formBody.initialItem!,
                      quantity: formBody.initialQuantity ?? 0,
                      cost: 0,
                    },
                  ];
              const updatedFinalItems = [
                {
                  ...finalItems[0],
                  initialItems: updatedInitialItems,
                },
              ];
              setFinalItems(updatedFinalItems);
              setIsSideBarOpen(false);
              setItemToEdit(undefined);
              setErrorMessage("");
              onChange(updatedFinalItems, true);
            } else {
              setErrorMessage(i18n.t.processingRequests.errors.missingFinalItem);
            }
          }}
        />
      </>
    );
  },
);

export default ItemsForm;
